import { ref, computed, type Component } from 'vue'
import { defineStore } from 'pinia'
import type { Modal, ModalOptions, ModalResult } from '../types/modal.types'

const DEFAULT_OPTIONS: Partial<ModalOptions> = {
  width: '500px',
  scrollable: true,
  closable: true,
  closeOnEsc: true,
  closeOnClickOutside: true,
}

export const useModalStore = defineStore('core-modal', () => {
  const modals = ref<Modal[]>([])

  // Mounted ModalContainer instances. Several can coexist — a layout and a
  // nested sub-layout both mount one — so only the first registered renders,
  // otherwise every modal would be drawn once per container.
  const containers = ref<string[]>([])

  const currentModal = computed(() => modals.value[modals.value.length - 1] ?? null)
  const hasModals = computed(() => modals.value.length > 0)
  const primaryContainerId = computed(() => containers.value[0] ?? null)

  function generateId(): string {
    return `modal-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  function registerContainer(): string {
    const id = `container-${generateId()}`
    containers.value.push(id)
    return id
  }

  function unregisterContainer(id: string): void {
    const index = containers.value.indexOf(id)
    if (index !== -1) {
      containers.value.splice(index, 1)
    }
  }

  function open<T = unknown>(
    title: string,
    component: Component,
    options: ModalOptions = {}
  ): Promise<ModalResult<T>> {
    return new Promise((resolve) => {
      const id = generateId()
      const mergedOptions = { ...DEFAULT_OPTIONS, ...options }

      const modal: Modal = {
        id,
        title,
        component,
        options: mergedOptions,
        resolve: resolve as (result: ModalResult) => void,
      }

      modals.value.push(modal)
    })
  }

  function close(data?: unknown): void {
    const modal = modals.value.pop()
    if (modal) {
      const result: ModalResult = { confirmed: false, data }
      modal.options.onClose?.(data)
      modal.resolve(result)
    }
  }

  function confirm(data?: unknown): void {
    const modal = modals.value.pop()
    if (modal) {
      const result: ModalResult = { confirmed: true, data }
      modal.options.onConfirm?.(data)
      modal.resolve(result)
    }
  }

  function closeAll(): void {
    while (modals.value.length > 0) {
      close()
    }
  }

  function closeById(id: string, data?: unknown): void {
    const index = modals.value.findIndex((m) => m.id === id)
    if (index !== -1) {
      const modal = modals.value.splice(index, 1)[0]
      const result: ModalResult = { confirmed: false, data }
      modal.options.onClose?.(data)
      modal.resolve(result)
    }
  }

  function confirmById(id: string, data?: unknown): void {
    const index = modals.value.findIndex((m) => m.id === id)
    if (index !== -1) {
      const modal = modals.value.splice(index, 1)[0]
      const result: ModalResult = { confirmed: true, data }
      modal.options.onConfirm?.(data)
      modal.resolve(result)
    }
  }

  return {
    modals,
    currentModal,
    hasModals,
    primaryContainerId,
    registerContainer,
    unregisterContainer,
    open,
    close,
    confirm,
    closeAll,
    closeById,
    confirmById,
  }
})
